/**
 * KimiChatService - Service für die Kommunikation mit der KI "Kimi"
 * 
 * @description Stellt Methoden bereit, um Nachrichten an die KI zu senden
 * und Antworten zu erhalten. Aktuell mit Mock-Implementierung, vorbereitet
 * für spätere API-Integration.
 */

/**
 * Simuliert eine API-Verzögerung
 * @param {number} ms - Millisekunden
 * @returns {Promise}
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Mock-Antworten basierend auf Keywords in der Nachricht
 * @param {string} message - Die User-Nachricht
 * @returns {string} Antwort von Kimi
 */
function getMockResponse(message) {
  const lowerMessage = message.toLowerCase();

  // Begrüßungen
  if (lowerMessage.includes("hallo") || lowerMessage.includes("hi") || lowerMessage.includes("hey")) {
    return "Hallo! Schön, dass du da bist. Wie kann ich dir heute helfen?";
  }

  // Fragen zu Funktionen
  if (lowerMessage.includes("was kannst du") || lowerMessage.includes("was kann")) {
    return "Ich kann dir bei verschiedenen Fragen zu deinem Dashboard helfen. Du kannst mich zum Beispiel fragen:\n\n• Wie funktioniert die Umsatzplanung?\n• Was bedeutet der Runway?\n• Wie lese ich die Charts?\n\nFrag mich einfach, was du wissen möchtest!";
  }

  // Fragen zu Dashboard
  if (lowerMessage.includes("dashboard") || lowerMessage.includes("übersicht")) {
    return "Das Dashboard zeigt dir eine Übersicht über deine wichtigsten Kennzahlen. Du findest dort:\n\n• Financial Health Score\n• Market Traction Score\n• Runway-Verlauf\n• Umsatz-Übersicht\n\nWelche Metrik interessiert dich besonders?";
  }

  // Fragen zu Runway
  if (lowerMessage.includes("runway") || lowerMessage.includes("laufzeit")) {
    return "Der Runway zeigt dir, wie viele Monate dein Unternehmen mit dem aktuellen Cash-Bestand und der aktuellen Burn Rate noch überleben kann. Ein positiver Wert bedeutet, dass du noch Geld hast, ein negativer Wert zeigt, dass du bereits im Minus bist.";
  }

  // Fragen zu Umsatzplanung
  if (lowerMessage.includes("umsatz") || lowerMessage.includes("revenue") || lowerMessage.includes("umsatzplanung")) {
    return "In der Umsatzplanung kannst du deine erwarteten Umsätze pro Monat und Produkt eintragen. Die Daten werden automatisch gespeichert und können für Prognosen verwendet werden.";
  }

  // Fragen zu Kostenplanung
  if (lowerMessage.includes("kosten") || lowerMessage.includes("ausgaben") || lowerMessage.includes("kostenplanung")) {
    return "Die Kostenplanung hilft dir, alle deine Ausgaben zu kategorisieren und zu planen. Du kannst verschiedene Kostenkategorien wie Technik, Marketing, Personal etc. anlegen und monatlich planen.";
  }

  // Fragen zu Financial Health
  if (lowerMessage.includes("financial health") || lowerMessage.includes("finanzielle gesundheit")) {
    return "Der Financial Health Score bewertet die finanzielle Gesundheit deines Unternehmens. Er berücksichtigt Faktoren wie Runway, Burn Rate, Cash Position, MRR und LTV/CAC Ratio.";
  }

  // Fragen zu Market Traction
  if (lowerMessage.includes("market traction") || lowerMessage.includes("marktdurchdringung")) {
    return "Market Traction misst, wie erfolgreich dein Produkt am Markt ankommt. Der Score basiert auf Metriken wie MRR-Wachstum, Kundenwachstum, Retention Rate und NPS.";
  }

  // Fragen zu Banking
  if (lowerMessage.includes("banking") || lowerMessage.includes("konto") || lowerMessage.includes("kontoumsätze")) {
    return "Im Banking-Bereich kannst du alle deine Bankkonten, Kontostände und Transaktionen einsehen. Die Daten werden automatisch aktualisiert und helfen dir, den Überblick über deine Finanzen zu behalten.";
  }

  // Standard-Antworten
  if (lowerMessage.includes("danke") || lowerMessage.includes("vielen dank")) {
    return "Gerne geschehen! Gibt es noch etwas, wobei ich dir helfen kann?";
  }

  if (lowerMessage.includes("nein") || lowerMessage.includes("nicht") || lowerMessage.includes("keine")) {
    return "Alles klar! Falls du später Fragen hast, bin ich immer für dich da. Viel Erfolg!";
  }

  // Fallback-Antwort
  return "Das ist eine interessante Frage! Leider kann ich dir dazu noch keine spezifische Antwort geben. Du kannst mich aber gerne zu folgenden Themen fragen:\n\n• Dashboard und Metriken\n• Umsatz- und Kostenplanung\n• Financial Health\n• Market Traction\n• Banking und Kontoumsätze\n\nWie kann ich dir sonst helfen?";
}

/**
 * Sendet eine Nachricht an die KI "Kimi" und erhält eine Antwort
 * 
 * @param {string} message - Die Nachricht des Users
 * @returns {Promise<string>} Die Antwort von Kimi
 * @throws {Error} Bei Netzwerk- oder Serverfehlern
 * 
 * @example
 * const response = await sendMessageToKimi("Was ist der Runway?");
 * console.log(response); // "Der Runway zeigt dir..."
 */
export async function sendMessageToKimi(message) {
  if (!message || typeof message !== "string" || message.trim().length === 0) {
    throw new Error("Nachricht darf nicht leer sein");
  }

  try {
    // TODO: Ersetze durch echten API-Call
    // const response = await fetch('/api/kimi/chat', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${getAuthToken()}`
    //   },
    //   body: JSON.stringify({ message })
    // });
    // 
    // if (!response.ok) {
    //   throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    // }
    // 
    // const data = await response.json();
    // return data.response;

    // Mock-Implementierung: Simuliere API-Verzögerung
    await delay(800 + Math.random() * 400); // 800-1200ms

    // Generiere Mock-Antwort
    const response = getMockResponse(message.trim());

    return response;
  } catch (error) {
    console.error("[KimiChatService] Fehler beim Senden der Nachricht:", error);
    throw new Error("Fehler beim Kommunizieren mit Kimi. Bitte versuche es später erneut.");
  }
}

/**
 * Sendet mehrere Nachrichten in einem Kontext (für spätere Konversations-API)
 * 
 * @param {Array<Object>} conversationHistory - Array von Nachrichten mit {role: 'user'|'kimi', content: string}
 * @returns {Promise<string>} Die Antwort von Kimi
 */
export async function sendMessageWithContext(conversationHistory) {
  // TODO: Implementiere für echte API mit Konversations-Kontext
  const lastMessage = conversationHistory[conversationHistory.length - 1];
  if (lastMessage && lastMessage.role === "user") {
    return await sendMessageToKimi(lastMessage.content);
  }
  throw new Error("Letzte Nachricht muss vom User sein");
}
